import _ from 'lodash'
import {
  diffFields,
  diffObjects,
  type DiffFieldType,
  type FieldRegistryEntry,
  type ObjectDiffChange,
  valuesDiffer,
} from '~/functions/object-diff'
import type { ImpactType, MetricAssignmentType } from '~/types/types'

export const STRATEGY_DIFF_FIELD_KEYS = [
  'companyVision',
  'problemStatement',
  'targetGroup',
  'inputsActivities',
  'solutionsOutput',
  'shortTermOutcomes',
  'longTermImpacts',
  'riskReboundEffects',
] as const

export type StrategyDiffFieldKey = (typeof STRATEGY_DIFF_FIELD_KEYS)[number]

const STRATEGY_DIFF_REGISTRY: FieldRegistryEntry<StrategyDiffFieldKey>[] =
  STRATEGY_DIFF_FIELD_KEYS.map((key) => ({ key, type: 'markdown' }))

export const CARRY_SCALAR_DIFF_FIELD_KEYS = [
  'carryRemarks',
  'startingYear',
  'durationYears',
  'initialInvestmentDate',
] as const

export type CarryScalarDiffFieldKey = (typeof CARRY_SCALAR_DIFF_FIELD_KEYS)[number]

const CARRY_SCALAR_DIFF_REGISTRY: FieldRegistryEntry<CarryScalarDiffFieldKey>[] = [
  { key: 'carryRemarks', type: 'text' },
  { key: 'startingYear', type: 'number' },
  { key: 'durationYears', type: 'number' },
  { key: 'initialInvestmentDate', type: 'text' },
]

function metricAssignmentComparableSlice(
  assignment: MetricAssignmentType,
): Record<string, unknown> {
  const valuesByPeriod: Record<string, { actual: unknown; target: unknown }> = {}
  for (const value of assignment.metricValues ?? []) {
    valuesByPeriod[value.period] = {
      actual: value.actual ?? null,
      target: value.target ?? null,
    }
  }

  return {
    comment: assignment.comment ?? null,
    customTargetWeight: assignment.customTargetWeight ?? null,
    ...Object.fromEntries(
      Object.entries(valuesByPeriod).flatMap(([period, entry]) => [
        [`${period} actual`, entry.actual],
        [`${period} target`, entry.target],
      ]),
    ),
  }
}

export function diffMetricAssignment(
  baseline: MetricAssignmentType | null | undefined,
  current: MetricAssignmentType | null | undefined,
): ObjectDiffChange[] {
  const baselineSlice = baseline
    ? metricAssignmentComparableSlice(baseline)
    : {}
  const currentSlice = current ? metricAssignmentComparableSlice(current) : {}
  return diffObjects(baselineSlice, currentSlice)
}

function metricAssignmentComparableSliceForEquality(
  assignment: MetricAssignmentType,
): Record<string, unknown> {
  return {
    id: assignment.id,
    ...metricAssignmentComparableSlice(assignment),
  }
}

export function diffStrategyFields(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
) {
  return diffFields(
    baseline as Record<string, unknown> | undefined,
    current as Record<string, unknown> | undefined,
    STRATEGY_DIFF_REGISTRY,
  )
}

export function getStrategyChangedFieldKeys(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
): Set<StrategyDiffFieldKey> {
  return new Set(
    Object.keys(diffStrategyFields(baseline, current)) as StrategyDiffFieldKey[],
  )
}

export function diffCarryScalarFields(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
) {
  return diffFields(
    baseline as Record<string, unknown> | undefined,
    current as Record<string, unknown> | undefined,
    CARRY_SCALAR_DIFF_REGISTRY,
  )
}

export function getCarryScalarChangedFieldKeys(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
): Set<CarryScalarDiffFieldKey> {
  return new Set(
    Object.keys(diffCarryScalarFields(baseline, current)) as CarryScalarDiffFieldKey[],
  )
}

export function getChangedMetricAssignmentIds(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
): Set<number> {
  const baselineById = new Map<number, Record<string, unknown>>()
  for (const assignment of baseline?.metricAssignments ?? []) {
    if (assignment.id == null) continue
    baselineById.set(assignment.id, metricAssignmentComparableSliceForEquality(assignment))
  }

  const changed = new Set<number>()
  for (const assignment of current?.metricAssignments ?? []) {
    if (assignment.id == null) continue
    const baselineSlice = baselineById.get(assignment.id)
    const currentSlice = metricAssignmentComparableSliceForEquality(assignment)
    if (!baselineSlice) {
      changed.add(assignment.id)
      continue
    }
    if (!_.isEqual(baselineSlice, currentSlice)) {
      changed.add(assignment.id)
    }
  }

  for (const assignment of baseline?.metricAssignments ?? []) {
    if (assignment.id == null) continue
    const stillPresent = (current?.metricAssignments ?? []).some(
      (item) => item.id === assignment.id,
    )
    if (!stillPresent) changed.add(assignment.id)
  }

  return changed
}

export function hasCarryComparisonChanges(
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
): boolean {
  if (!baseline || !current) return false
  if (getCarryScalarChangedFieldKeys(baseline, current).size > 0) return true
  return getChangedMetricAssignmentIds(baseline, current).size > 0
}

export function fieldDiffType(
  key: StrategyDiffFieldKey | CarryScalarDiffFieldKey,
): DiffFieldType {
  if ((CARRY_SCALAR_DIFF_FIELD_KEYS as readonly string[]).includes(key)) {
    const entry = CARRY_SCALAR_DIFF_REGISTRY.find((item) => item.key === key)
    return entry?.type ?? 'text'
  }
  return 'markdown'
}

export function isFieldChanged(
  key: StrategyDiffFieldKey | CarryScalarDiffFieldKey,
  baseline: ImpactType | null | undefined,
  current: ImpactType | null | undefined,
): boolean {
  return valuesDiffer(
    baseline?.[key as keyof ImpactType],
    current?.[key as keyof ImpactType],
    fieldDiffType(key),
  )
}
